import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMaterialSignedUrl } from '../../../features/materials/api';
import type { Scenario } from './types';
import styles from './ScenarioGenerator.module.css';

/** 一括再生の対象: ナレーション音声があり、除外していないシーンの素材 id (シーン順)。 */
export function narrationPlayAllMaterialIds(scenario: Pick<Scenario, 'scenes'>): string[] {
  return [...(scenario.scenes || [])]
    .sort((a, b) => a.order - b.order)
    .filter((s) => !!s.narration_material_id && !s.narration_excluded)
    .map((s) => s.narration_material_id as string);
}

export type NarrationPlayAllStatus = 'idle' | 'loading' | 'playing' | 'paused';

/**
 * 各シーンのナレーション音声をシーン順に続けて再生する状態と操作。
 * 展開時の「すべてのナレーションを再生」ボタンと、折りたたみ時の進捗リングの中央の再生ボタンが使う。
 * toggle: 停止中は先頭から再生・再生中は一時停止・一時停止中は続きから再開。
 */
export function useNarrationPlayAll(scenario: Pick<Scenario, 'scenes'>) {
  const materialIds = narrationPlayAllMaterialIds(scenario);
  const idsKey = materialIds.join(',');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const idsRef = useRef<string[]>(materialIds);
  idsRef.current = materialIds;
  const [status, setStatus] = useState<NarrationPlayAllStatus>('idle');

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    setStatus('idle');
  }, []);

  // 素材が入れ替わった (再生成した・除外を切り替えた) / アンマウント時は再生を止める
  useEffect(() => {
    stop();
  }, [idsKey, stop]);
  useEffect(() => () => stop(), [stop]);

  const playAt = useCallback(async (index: number) => {
    const ids = idsRef.current;
    if (index >= ids.length) {
      stop();
      return;
    }
    let url: string | null = null;
    try {
      url = await fetchMaterialSignedUrl(ids[index]!, true).catch(() => fetchMaterialSignedUrl(ids[index]!, false));
    } catch {
      url = null;
    }
    if (!url) {
      void playAt(index + 1);
      return;
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => void playAt(index + 1);
    audio.onerror = () => void playAt(index + 1);
    audio.play().then(() => setStatus('playing')).catch(() => stop());
  }, [stop]);

  const toggle = useCallback(() => {
    if (status === 'playing' && audioRef.current) {
      audioRef.current.pause();
      setStatus('paused');
      return;
    }
    if (status === 'paused' && audioRef.current) {
      audioRef.current.play().then(() => setStatus('playing')).catch(() => stop());
      return;
    }
    if (status === 'loading' || idsRef.current.length === 0) return;
    setStatus('loading');
    void playAt(0);
  }, [status, playAt, stop]);

  return { playable: materialIds.length > 0, status, toggle };
}

export type NarrationPlayAll = ReturnType<typeof useNarrationPlayAll>;

/**
 * ナレーション セクションの展開時に置く「すべてのナレーションを再生」ボタン (アイコンのみ)。
 * player は折りたたみ時の進捗リングと共用する (useNarrationPlayAll の戻り値・二重再生しない)。
 * 再生中は一時停止でき、再度押すと続きから再開する。再生できる音声が 1 本も無いときは何も描画しない。
 */
export function NarrationPlayAllButton({ player, disabled }: { player: NarrationPlayAll; disabled?: boolean }) {
  const { playable, status, toggle } = player;

  if (!playable) return null;

  const playing = status === 'playing';
  return (
    <button
      type="button"
      className={`${styles.voicePreviewBtn} ${playing || status === 'paused' ? styles.voicePreviewBtnActive : ''}`}
      onClick={(e) => { e.stopPropagation(); toggle(); }}
      disabled={disabled || status === 'loading'}
      data-tooltip={playing ? '一時停止' : 'すべてのナレーションを再生'}
      aria-label={playing ? 'ナレーションを一時停止' : 'すべてのナレーションを再生'}
    >
      {playing ? (
        <svg width="16" height="16" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <rect x="5" y="4.5" width="2" height="7" rx="0.5" fill="currentColor" />
          <rect x="9" y="4.5" width="2" height="7" rx="0.5" fill="currentColor" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}
